import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import capitalize from 'lodash/capitalize';
import i18n from 'i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { Button } from 'uikit/buttons';
import { Loading } from 'uikit/loaders';
import { Menu } from 'uikit/menus';
import { TextInput } from 'uikit/inputs';
import { Typo } from 'uikit/content';

import getOptionValue from '../helpers/getOptionValue';

import useAsyncOptions from '../hooks/useAsyncOptions';
import useDebounce from '../hooks/useDebounce';

import AsyncFilterOption from './asyncFilterOption';
import FilterOption from './filterOption';

import styles from '../styles/filter.module.scss';

const Filter = ({
  dropdownPosition = 'right',
  isAsync = false,
  isDisabled = false,
  isSingleSelect = false,
  name = '',
  onSelectionUpdate = () => {},
  options: fixedOptions = [],
  param = '',
  selection = [],
  extraParams = {},
  variant = 'default',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState(inputValue);
  const debouncedSetSearchQuery = useDebounce(setSearchQuery, 600);
  const scrollerId = 'filter-scroller';

  const { asyncOptions, placeholder, selectedLabel } = useAsyncOptions({
    isEnabled: isAsync,
    param,
    searchQuery,
    selection,
    extraParams,
  });

  const options = isAsync
    ? asyncOptions.data?.pages.flatMap(({ data }) => data).filter(Boolean) || []
    : fixedOptions;

  const selectedOptions = options.filter(({ value }) =>
    selection.includes(getOptionValue(value, options)),
  );

  const label = selectedLabel.data || selectedOptions[0]?.label;
  const selectedOptionsCount = isAsync ? selection.length : selectedOptions.length;
  const isActive = Boolean(selectedOptionsCount);
  const selectedValues = selectedOptions.map(({ value }) => value);

  const getTitleElement = (text, isBold) => (
    <Typo
      className={classnames(styles.name, {
        [styles.isDisabled]: isDisabled,
      })}
      element="span"
      variant="b01"
      weight={isBold ? 'medium' : ''}
    >
      {text}
    </Typo>
  );

  const title = (
    <>
      {getTitleElement(label || name, isActive)}
      {selectedOptionsCount > 1 && getTitleElement(` (+${selectedOptionsCount - 1})`)}
    </>
  );

  const getActiveFilterLabel = () =>
    i18n.t('active-filter-label', {
      count: selectedOptionsCount,
      label,
      moreCount: selectedOptionsCount - 1,
    });

  const getTooltip = () => {
    if (!isActive || !label) return;

    return variant === 'drawer' ? getActiveFilterLabel() : name;
  };

  const handleInputValueChange = (value) => {
    setInputValue(value);
    debouncedSetSearchQuery(value);
  };

  const handleInputCloseButtonClick = () => {
    handleInputValueChange('');
  };

  const renderOptions = () => {
    const Option = isAsync ? AsyncFilterOption : FilterOption;

    return options.map(({ value, ...option }) => (
      <li key={value}>
        <Option
          code={option.code}
          id={option.id}
          isSelected={selectedValues.includes(value)}
          isSingleSelect={isSingleSelect}
          name={option.label}
          onChange={(checked) =>
            onSelectionUpdate({
              isSelected: checked,
              isSingleSelect,
              key: param,
              value: getOptionValue(value, options),
            })
          }
          value={value}
        />
      </li>
    ));
  };

  const renderScroller = () => {
    if (asyncOptions.isLoading) return <Loading className={styles.loader} />;

    const itemsCount = options.length;

    if (!itemsCount)
      return (
        <Typo className={styles.empty} variant="s02">
          {i18n.t('no-results-found')}
        </Typo>
      );

    return (
      <InfiniteScroll
        dataLength={itemsCount}
        hasMore={asyncOptions.hasNextPage}
        loader={<Loading className={styles.loader} />}
        next={asyncOptions.fetchNextPage}
        scrollableTarget={scrollerId}
      >
        <ul className={styles.options}>{renderOptions()}</ul>
      </InfiniteScroll>
    );
  };

  const renderTitle = () => {
    if (variant === 'default') return title;

    return !label ? (
      getTitleElement(name, isActive)
    ) : (
      <Typo noWrap variant="s02">
        {getTitleElement(i18n.t('active-filter-label-prefix', { name }), true)}
        {getTitleElement(getActiveFilterLabel())}
      </Typo>
    );
  };

  return (
    <div className={classnames(styles.container, styles[`isVariant${capitalize(variant)}`])}>
      <Menu
        className={classnames(styles.element, styles.filter, {
          [styles.isActive]: isActive,
          [styles.isAsync]: isAsync,
        })}
        component={
          <Button
            icon={<KeyboardArrowDownOutlinedIcon />}
            iconPosition="right"
            isDisabled={isDisabled}
            size="small"
            variant="outlined"
          >
            {renderTitle()}
          </Button>
        }
        contentClassName={styles.dropdown}
        horizontalPosition={dropdownPosition}
        shouldCloseOnTab={false}
        tooltip={getTooltip()}
      >
        {!isAsync ? (
          renderOptions()
        ) : (
          <>
            <li className={styles.search}>
              <TextInput
                icon={<SearchOutlinedIcon />}
                id="filter-search"
                isDisabled={isDisabled}
                onChange={handleInputValueChange}
                onClick={handleInputCloseButtonClick}
                placeholder={placeholder}
                shouldShowCloseButton={Boolean(inputValue.length)}
                size="small"
                value={inputValue}
              />
            </li>
            <li className={styles.scroller} id={scrollerId}>
              {renderScroller()}
            </li>
          </>
        )}
      </Menu>
    </div>
  );
};

Filter.propTypes = {
  dropdownPosition: PropTypes.oneOf(['left', 'right']),
  isAsync: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSingleSelect: PropTypes.bool,
  name: PropTypes.string,
  onSelectionUpdate: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
    }),
  ),
  param: PropTypes.string,
  selection: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  ),
  extraParams: PropTypes.shape({}),
  variant: PropTypes.oneOf(['default', 'drawer']),
};

export default React.memo(Filter);
